import { useIsAuthenticated, useSessionUserId } from 'lib/auth/auth.hooks';
import { getDeviceId } from 'lib/utils/device';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Action,
  type DisplayAuctionResult,
  getDisplayAdWinners,
  MediaPlacementType,
  notifyDisplayAdClick,
  notifyDisplayAdImpression,
} from 'lib/sdks/ad-server';
import { Experiment, useOptimizely } from 'lib/sdks/optimizely';

const BillboardCarousel = dynamic(
  () => import('components/TradeCard/BillboardCarousel/BillboardCarousel'),
);

export type DisplayAdBannerProps = {
  postcode?: string;
  categoryId?: number;
};

const DisplayAdBanner = ({ postcode, categoryId }: DisplayAdBannerProps) => {
  const { decide, loading: optimizelyLoading } = useOptimizely();
  const [results, setResults] = useState<DisplayAuctionResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const userId = useSessionUserId();
  const [deviceId, setDeviceId] = useState<string | null>(null);

  const runAuction = useCallback(async () => {
    if (isLoading || results !== null || !deviceId) return;

    setIsLoading(true);
    try {
      const result = await getDisplayAdWinners({
        user: {
          id: userId || deviceId,
          isAuthenticated,
          userAgent: window?.navigator?.userAgent || '',
        },
        targeting: {
          category: categoryId || 0,
          location: postcode || '',
        },
        maxSponsoredItems: 3,
        placementType: MediaPlacementType.SEARCH_RESULT,
      });

      setResults(result);
    } catch (error) {
      // ignore errors
      setResults(null);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    results,
    categoryId,
    postcode,
    isAuthenticated,
    userId,
    deviceId,
  ]);

  const allowDisplayAd = useMemo(() => {
    if (optimizelyLoading) return false;

    const decision = decide(Experiment.RAQ_ENTRY_POINT);
    return (
      decision?.variationKey === 'display_ads_enabled' && decision?.enabled
    );
  }, [decide, optimizelyLoading]);

  const billboardItems = useMemo(() => {
    if (!results || !results.winners || !results.winners.length) return [];

    return results.winners
      .map(winner => ({
        callToAction: winner.creatives[0]?.callToAction || '',
        headline: winner.creatives[0]?.headline || '',
        body: winner.creatives[0]?.body || '',
        imageUrl: winner.creatives[0]?.imageUrl || '',
        targetUrl: winner.targetUrl || '',
      }))
      .filter(item => item.callToAction && item.headline && item.targetUrl);
  }, [results]);

  const handleImpression = (index: number) => {
    if (!results || results.winners.length <= index) return;

    const winner = results.winners[index];
    if (winner.trackingId) {
      notifyDisplayAdImpression(winner.trackingId, index);
    }
  };

  const handleClick = (index: number) => {
    if (!results || results.winners.length <= index) return;

    const winner = results.winners[index];
    if (winner.trackingId) {
      notifyDisplayAdClick(Action.LINK, winner.trackingId, index);
    }
  };

  useEffect(() => {
    if (allowDisplayAd && !results && !isLoading) {
      runAuction();
    }
  }, [allowDisplayAd, isLoading, results, runAuction]);

  useEffect(() => {
    setDeviceId(getDeviceId());
  }, []);

  if (!allowDisplayAd) return null;

  return (
    <BillboardCarousel
      billboardItems={billboardItems}
      onAdImpression={handleImpression}
      onAdClick={handleClick}
    />
  );
};

export default DisplayAdBanner;
